import React from 'react'
import PropTypes from 'prop-types'

import { useForm } from 'react-hook-form'

import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import CircularProgress from '@material-ui/core/CircularProgress'

// Icons
import SendIcon from '@material-ui/icons/Send'
import CancelIcon from '@material-ui/icons/Cancel'

import ValidatedTextField from 'components/HookFormValidation/ValidatedTextField'
import ValidatedDropDown from 'components/HookFormValidation/ValidatedDropDown'

import {
  updatePosUser,
  updatePortalUser,
  publishPosUsers
} from '../../layouts/Aws.js'

export default function UserDialog (props) {
  const [saving, setSaving] = React.useState(false)
  const [createUserButtonText, setCreateUserButtonText] = React.useState('Create User')
  const [dialogSavingText, setDialogSavingText] = React.useState('Saving')
  const [editMode, setEditMode] = React.useState(false) // if editMode is true, we are updating an existing user

  React.useEffect(() => {
    if (props.selectedUser !== null) {
      console.log('setting edit mode to true')
      setEditMode(true)
    } else {
      setEditMode(false)
    }
  }, [props.selectedUser])

  const { handleSubmit, control, setValue, reset, watch, trigger, formState: { errors } } = useForm({
    defaultValues: {
      userId: null, // if userId gets set, we know we must edit an existing user, rather than create a new one
      username: '',
      email: '',
      userRole: '',
      pin: '',
      pinConfirmation: ''
    },
    mode: 'onTouched'
  })

  const watchPin = watch('pin')
  const watchPinConfirmation = watch('pinConfirmation')

  React.useEffect(() => {
    if (watchPin.length > 0 && watchPinConfirmation.length > 0) {
      trigger('pinConfirmation')
    }
  }, [watchPin])

  React.useEffect(() => {
    if (props.selectedUser !== null) {
      setValue('userId', props.selectedUser.userId)
      setValue('username', props.selectedUser.username)
      setValue('email', props.selectedUser.email)
      setValue('userRole', props.selectedUser.userRoleId)
    }
  }, [props.selectedUser, setValue, open])

  const roles = props.roles.map(({ userRoleId, roleDescription }) => {
    return ({
      value: userRoleId,
      displayString: roleDescription
    })
  })

  // reset the form upon open / close
  React.useEffect(() => {
    if (!props.open) {
      reset() // reset the form to default values upon close
    }
  }, [props.open])

  React.useEffect(() => {
    if (editMode) {
      setCreateUserButtonText('Update')
    } else {
      if (props.userType === 'Portal') {
        setCreateUserButtonText('Invite')
      } else {
        setCreateUserButtonText('Create User')
      }
    }
  }, [props.userType, editMode])

  const handleClose = (event, reason) => {
    props.onClose(event, reason)
  }

  const submitForm = (data) => {
    setSaving(true)
    setDialogSavingText('Saving')

    if (props.userType === 'Portal') {
      const userData = {
        userId: data.userId,
        username: data.username,
        email: data.email
      }

      updatePortalUser(props.site, userData).then(res => {
        if (res.status === 200) {
          props.onSave(res, editMode)
        } else if (res.status === 409) {
          props.onSave(res)
        } else {
          props.onSave(res)
        }

        setSaving(false)
      })
    } else if (props.userType === 'POS') {
      const userData = {
        userId: data.userId,
        username: data.username,
        role: data.userRole,
        pin: data.pin
      }

      updatePosUser(props.site, userData).then(async (res) => {
        if (res.status === 200) {
          setDialogSavingText('Publishing')
          var publishResponse = await publishPosUsers(props.site)

          props.onSave(publishResponse, editMode)
        } else {
          props.onSave(res)
        }

        setSaving(false)
      })
    }
  }

  const dialogContent = () => {
    if (saving) {
      return (
        <>
          <div style={{ textAlign: 'center' }}>
            <CircularProgress/>
            <div>{dialogSavingText}</div>
          </div>
        </>
      )
    } else {
      if (props.userType === 'Portal') {
        return (
          <>
            <ValidatedTextField
              control={control}
              errors={errors}
              name="username"
              label="Username"
              rules={{
                minLength: {
                  value: 2,
                  message: 'Username must be between 2 and 100 characters long'
                },
                maxLength: {
                  value: 100,
                  message: 'Username must be between 2 and 100 characters long'
                },
                required: 'Username is required'
              }}
              margin="dense"
              autoComplete="new-password"
            />
            <ValidatedTextField
              control={control}
              errors={errors}
              name="email"
              label="Email"
              disabled={editMode}
              rules={{
                pattern: {
                  value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                  message: 'Email address must be valid'
                },
                required: 'Email address is required'
              }}
              margin="dense"
              autoComplete="new-password"
            />
          </>
        )
      }

      if (props.userType === 'POS') {
        return (
          <>
            <ValidatedTextField
              control={control}
              errors={errors}
              name="username"
              label="Username"
              rules={{
                minLength: {
                  value: 2,
                  message: 'Username must be between 2 and 10 characters long'
                },
                maxLength: {
                  value: 10,
                  message: 'Username must be between 2 and 10 characters long'
                },
                required: 'Username is required'
              }}
              margin="dense"
              autoComplete="new-password"
            />
            <ValidatedDropDown
              name="userRole"
              label="User Role"
              control={control}
              errors={errors}
              options={roles}
              rules={{
                required: 'User Role is required'
              }}
              margin="dense"
            />
            <ValidatedTextField
              control={control}
              errors={errors}
              name="pin"
              label={ editMode ? 'Update PIN' : 'PIN' }
              rules={{
                pattern: {
                  value: /^\d{6}$/,
                  message: 'PIN must be 6 digits'
                },
                required: !editMode && 'PIN is required'
              }}
              margin="dense"
              fullWidth={false}
              type="password"
              autoComplete="new-password"
              helperText={ editMode ? 'Leave blank if you don\'t wish to update' : ''}
            />
            <br/>
            <ValidatedTextField
              control={control}
              errors={errors}
              name="pinConfirmation"
              label={ editMode ? 'Confirm Update PIN' : 'Confirm PIN' }
              rules={{
                validate: (value) => {
                  if (watch('pin') !== value) {
                    return ('PINs must match')
                  }
                }
              }}
              margin="dense"
              fullWidth={false}
              type="password"
              autoComplete="new-password"
            />
          </>
        )
      }
    }
  }

  return (
    <Dialog maxWidth="sm" fullWidth={true} onClose={handleClose} open={props.open}>
      <DialogTitle style={ { textAlign: 'left' } }>
        {editMode ? 'Edit' : 'Add New'} {props.userType} User {props.name !== null ? 'for ' + props.name : '' }
      </DialogTitle>
      <DialogContent dividers>
        <form id="newUserForm" noValidate autoComplete="new-password" onSubmit={handleSubmit((data) => submitForm(data))}>
          {
            dialogContent()
          }
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          form="newUserForm"
          autoFocus
          variant="contained"
          color="primary"
          disabled={saving}>
          <><SendIcon />&nbsp;</>
          {createUserButtonText}
        </Button>
        <Button
          variant="contained"
          onClick={handleClose}
          color="secondary"
          disabled={saving}>
          <CancelIcon />&nbsp;Cancel
        </Button>
      </DialogActions>
    </Dialog>)
}

UserDialog.propTypes = {
  site: PropTypes.string,
  name: PropTypes.string,
  userType: PropTypes.string,
  roles: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedUser: PropTypes.object
}
